import React, { useState, useMemo, cloneElement, forwardRef, useImperativeHandle } from 'react';
import { filterStringEmoji, isSupportAppShare } from '@/utils/tool';
import { useRouter } from 'next/router';
import qs from 'qs';
import dynamic from 'next/dynamic';

import { Base64 } from '@/utils/base64';
import { WeeeIcon } from '@/components/common/Icon';
import Constants from '@/constants/const';
import callAppFunction from '@/utils/bridge';
import useAutoSetWhatInfo, { ShareItemInterface } from './hooks/set-wechat-info';
import useLazy from '@/hooks/useLazy';
import FbHead from './fb-head';
import Loading from '@/components/common/Indicator/Loading';
import { isAndroid } from '@/utils/common/device';

const defaultRender = (data, shareIcon = 'icona-share-ios1', iconClassName) => {
  if (typeof shareIcon === 'object') return shareIcon;
  return (
    <WeeeIcon
      data-testid="btn-share"
      className={iconClassName}
      type={shareIcon}
      style={{ fontSize: 20 }}
      alt="share button image"
    />
  );
};

export const generateFormatData = (data: ShareItemInterface) => {
  return {
    share_content: [data]
  };
};

const ShareComponent = dynamic(() => import('./share-popup'), { ssr: false, loading: Loading });

const MainSharePopup = forwardRef(({ children, visible: propsVisible, onCancel, ...props }: any, ref) => {
  const {
    data: shareInfo = {},
    imageData = {},
    render = defaultRender,
    onBeforeShow,
    id,
    appShareEnable = true,
    onChooseMethod,
    onShareBtnClick,
    showFbHeadConfig = true,
    iconClassName,
    onSuccess,
    ogImageSquare
  } = props;

  const { query, locale } = useRouter();
  let childNode = render(shareInfo, props.shareIcon, iconClassName);
  const { show, hide, visible, loaded } = useLazy({
    visible: propsVisible
  });
  let [shareLanguage, setShareLanguage] = useState(
    locale || (query.site_lang as string) || (query.lang as string) || Constants.LANGUAGE_EN
  );
  const shareItem = useMemo(() => {
    const lang = shareLanguage === Constants.LANGUAGE_CHINESE_T ? Constants.LANGUAGE_ZHT_ALIAS : shareLanguage;
    let info =
      shareInfo?.share_content?.find(item => {
        return item.language === lang;
      }) || {};
    return { ...info, loading: info.loading ?? !info?.title };
  }, [JSON.stringify(shareInfo?.share_content), shareLanguage]);

  // 弹起之前判断
  const handleBefore = async () => {
    // app native share
    if (isSupportAppShare() && appShareEnable) {
      // NEW APP ONLY SHARE IMAGE
      if (shareInfo.share_type == 'image') {
        onBeforeShow?.(new Function());
        return callAppFunction(
          'shareContent',
          {
            data: new Base64().encode(JSON.stringify({ ...shareInfo }))
          },
          props.onSuccess
            ? function () {
                props.onSuccess();
              }
            : null
        );
      }
      shareInfo?.share_content?.forEach(item => {
        item.title = filterStringEmoji(item.title);
        item.description = filterStringEmoji(item.description);
      });
      onBeforeShow?.(new Function());

      let processedShareInfo = { ...shareInfo, ...{ page_url: location.href } };
      // 为安卓过滤 share_content，只传当前语言的
      if (!shareInfo?.show_language && isAndroid()) {
        const lang = shareLanguage === Constants.LANGUAGE_CHINESE_T ? Constants.LANGUAGE_ZHT_ALIAS : shareLanguage;
        const filteredContent = shareInfo.share_content.filter(item => item.language === lang);
        if (filteredContent.length > 0) {
          processedShareInfo.share_content = filteredContent;
        }
      }
      return callAppFunction(
        'shareContent',
        {
          data: new Base64().encode(JSON.stringify(processedShareInfo))
        },
        props.onSuccess
          ? function () {
              props.onSuccess();
            }
          : null
      );
    }
    // web
    onShareBtnClick?.();
    if (onBeforeShow) {
      return onBeforeShow(show);
    }
    show?.();
  };

  const shareUrl = useMemo(() => {
    const url = `${shareItem?.link_url}`;
    const t = new Date().getTime();
    const _url = url?.split('?')?.[0] || '';
    const _query = url?.split('?')?.[1] || '';
    const newQuery = qs.stringify({ ...qs.parse(_query), t }, { addQueryPrefix: true });

    return `${_url}${newQuery}`;
  }, [shareItem]);

  const handleCancel = () => {
    onCancel?.();
    hide?.();
  };

  useAutoSetWhatInfo(shareItem);

  useImperativeHandle(ref, () => ({
    show: onBeforeShow ? handleBefore : show,
    hide: hide
  }));

  return (
    <>
      {showFbHeadConfig && <FbHead ogImageSquare={ogImageSquare} shareUrl={shareUrl} shareItem={shareItem} />}

      {!!childNode &&
        cloneElement(render(shareInfo, props.shareIcon, iconClassName), {
          onClick: onBeforeShow ? handleBefore : show
        })}
      {loaded && (
        <ShareComponent
          {...props}
          id={id}
          imageData={imageData}
          onLanguageChange={lang => setShareLanguage(lang)}
          onChooseMethod={onChooseMethod}
          visible={visible}
          onCancel={handleCancel}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
});

export default MainSharePopup;
